import { PageCardProps } from '@design-system/page-card';
import { kebabCase } from 'lodash';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { headingBlockMapper } from '~/mappers/headingBlock';
import { imageMapper } from '~/mappers/image';
import {
  ItemListingPageCardRef,
  ItemListingRelatedPageCard,
} from '~/mappers/itemListing/models';

import { PageCardRef, RelatedPageCard } from './models';

const NO_DESCRIPTION = 'none';

const getDescription = (
  descriptionField: string,
  cmsDescription: string,
  descriptionFromHeader: string | null
): string | null => {
  if (descriptionField === NO_DESCRIPTION) return null;

  return cmsDescription || descriptionFromHeader;
};

export const relatedPageCardMapper = (
  {
    heading: { headline, descriptionRte },
    image,
    listingData: { label, description: cmsDescription },
    fullPath,
    categories,
  }: RelatedPageCard,
  {
    imageSettings,
    cardContentUI,
    variant,
    showLabels,
    descriptionField,
    showCategory,
    updatedAt,
  }: Omit<ItemListingRelatedPageCard, 'type' | 'typeName'>
): PageCardProps =>
  ({
    ...(image && {
      cardMedia: { image: imageMapper({ ...image, ...imageSettings }) },
    }),
    link: { component: TMPLinkWrap, props: { href: fullPath } },
    cardContent: {
      headingBlock: headingBlockMapper({
        ui: cardContentUI,
        headline,
        descriptionRte: getDescription(
          descriptionField,
          cmsDescription,
          descriptionRte
        ),
        detail: null,
        link: [],
        button: [],
      }),
      ...(showCategory &&
        categories[0]?.title && {
          category: categories[0].title,
        }),
    },
    ...(showLabels &&
      label[0]?.content && {
        cardLabel: {
          labelContent: label[0].content,
          srOnlyLabelPrefix: label[0].srPrefix,
          labelVariant: label[0].variant,
          labelSize: label[0].size,
        },
      }),
    variant,
    categories: categories.map(({ title }) => kebabCase(title)),
    updatedAt,
  } as PageCardProps);

export const pageCardRefMapper = (
  {
    pageHeader: {
      headingBlock: { headline, descriptionRte },
      imageElement,
    },
    fields: { fullPath },
    categories,
    listingData: { label, description: cmsDescription },
    listingImage,
    useFallbackImage,
  }: PageCardRef,
  {
    imageSettings,
    cardContentUI,
    align,
    mode,
    variant,
    descriptionField,
    horizontalDirection,
    showCategory,
    showLabels,
    updatedAt,
  }: Omit<ItemListingPageCardRef, 'type' | 'typeName'>
): PageCardProps => {
  const cardImage =
    useFallbackImage && listingImage[0] ? listingImage[0] : imageElement[0];

  return {
    ...(cardImage && {
      cardMedia: {
        image: imageMapper({ ...cardImage, ...imageSettings }),
      },
    }),
    link: { component: TMPLinkWrap, props: { href: fullPath } },
    cardContent: {
      headingBlock: headingBlockMapper({
        ui: cardContentUI,
        headline,
        descriptionRte: getDescription(
          descriptionField,
          cmsDescription,
          descriptionRte
        ),
        detail: null,
        link: [],
        button: [],
      }),
      ...(showCategory &&
        categories?.[0]?.title && {
          category: categories[0].title,
        }),
    },
    ...(showLabels &&
      label[0]?.content && {
        cardLabel: {
          labelContent: label[0].content,
          srOnlyLabelPrefix: label[0].srPrefix,
          labelVariant: label[0].variant,
          labelSize: label[0].size,
        },
      }),
    align,
    mode,
    variant,
    horizontalDirection,
    categories: categories.map(({ title }) => kebabCase(title)),
    updatedAt,
  } as PageCardProps;
};
